'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Individual Toast notification component with countdown timer and progress bar
 *
 * @param {Object} props - Component props
 * @param {Object} props.toast - Toast data object
 * @param {string} props.toast.title - Toast title
 * @param {string} props.toast.description - Toast message
 * @param {number} props.toast.duration - Auto-dismiss duration in ms
 * @param {string} props.toast.variant - Visual variant (info|success|error|warning)
 * @param {Function} props.onDone - Callback when toast is dismissed or expired
 * @param {number} props.index - Position in the toast stack
 * @param {number} props.initialIndex - Initial position (for animations)
 * @param {number} props.initialTotal - Initial total count (for animations)
 * @param {Object} props.focusRef - Shared ref for pause/resume on window focus
 * @param {boolean} props.active - Whether this toast's timer is currently running
 */
export default function Toast({
    toast,
    onDone,
    index,
    initialIndex,
    initialTotal,
    focusRef,
    active = false,
}) {
    const { title, description, duration } = toast;
    const variant = toast.variant || 'info';
    const [visible, setVisible] = useState(true);
    const [remaining, setRemaining] = useState(duration);
    const [isHovered, setIsHovered] = useState(false);
    const startRef = useRef(null);
    const rafRef = useRef(null);

    // Countdown timer (only runs when active and window is focused)
    useEffect(() => {
        // Only the front (active) toast runs its timer
        if (!active) return;
        let last = performance.now();
        startRef.current = performance.now();

        function tick(now) {
            if (!focusRef.current || isHovered) {
                last = now; // pause timer when not focused or hovered
                rafRef.current = requestAnimationFrame(tick);
                return;
            }
            const delta = now - last;
            last = now;
            setRemaining((r) => {
                const nr = r - delta;
                if (nr <= 0) {
                    setVisible(false);
                    // allow animation to finish
                    setTimeout(onDone, 300);
                }
                return Math.max(0, nr);
            });
            rafRef.current = requestAnimationFrame(tick);
        }

        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [onDone, focusRef, active, isHovered]);

    // Render complete class strings for Tailwind to detect
    let containerClasses =
        'pointer-events-auto w-80 bg-white shadow-xl rounded-lg overflow-hidden transform transition-all duration-300';
    let progressClasses = 'h-1 transition-all duration-100';
    let _leftBorderColor = '';

    if (variant === 'error') {
        _leftBorderColor = '#ef4444'; // red-500
        progressClasses += ' bg-red-500';
    } else if (variant === 'success') {
        _leftBorderColor = '#22c55e'; // green-500
        progressClasses += ' bg-green-500';
    } else if (variant === 'warning') {
        _leftBorderColor = '#eab308'; // yellow-500
        progressClasses += ' bg-yellow-500';
    } else {
        // info (default)
        _leftBorderColor = '#3b82f6'; // blue-500
        progressClasses += ' bg-blue-500';
    }

    containerClasses += visible
        ? ' opacity-100 translate-y-0'
        : ' opacity-0 translate-y-2';

    return (
        <output
            className={containerClasses}
            style={{
                border: 'none',
                display: 'block',
            }}
            aria-live="polite"
            aria-atomic="false"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="flex items-start gap-3 p-4">
                <div className="flex-1 min-w-0">
                    {title && (
                        <div
                            className="font-medium text-sm text-gray-900 mb-0.5"
                            title={title}
                        >
                            {title}
                        </div>
                    )}
                    {description && (
                        <div className="text-sm text-gray-600">
                            {description}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setVisible(false);
                        setTimeout(onDone, 200);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                    aria-label="Dismiss"
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <title>Dismiss</title>
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            </div>
            {/* Progress bar */}
            <div
                className={progressClasses}
                style={{
                    width: `${(remaining / toast.duration) * 100}%`,
                }}
            />
        </output>
    );
}
